import React, { useState } from 'react';
import { Heart, Camera, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { rapidResponseAPI } from '../services/api';

const ShareGoodNewsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [useAI, setUseAI] = useState(true); // Toggle for AI vs manual writing
  const [formData, setFormData] = useState({
    submitter_name: '',
    submitter_email: '',
    story_type: 'youth-sports',
    headline: '',
    story: '',
    key_details: '',
    manual_article: '' // For when user writes their own article
  });
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      toast({
        title: "Creating Your Story...",
        description: "AI is writing a beautiful article about this good news. This takes about 30 seconds."
      });

      // Prepare positive story for AI
      const positiveStoryPrompt = `
POSITIVE COMMUNITY STORY

Story Type: ${formData.story_type.replace('-', ' ')}
Headline: ${formData.headline}

The Story:
${formData.story}

Key Details to Include:
${formData.key_details}

This is a POSITIVE story celebrating the Central Coast community. Write it with enthusiasm and heart.
`;

      const responseData = {
        submitter_name: formData.submitter_name,
        submitter_email: formData.submitter_email,
        original_article_url: window.location.origin + '/good-news',
        original_article_title: 'Community Good News Story',
        your_story: positiveStoryPrompt,
        key_points: [
          'This is a positive community story',
          'Celebrate local achievements',
          'Highlight good deeds and heroes',
          formData.story_type
        ]
      };

      console.log('Submitting rapid response:', responseData);
      const result = await rapidResponseAPI.create(responseData);
      console.log('Rapid response result:', result);

      // Upload photos if any
      if (photos.length > 0) {
        toast({
          title: "Uploading Photos...",
          description: "Attaching your photos to the article"
        });
        
        const { evidenceAPI } = await import('../services/api');
        const articleId = result._id || result.id;
        
        try {
          await evidenceAPI.uploadMultiple(photos, articleId, formData.submitter_name, formData.submitter_email);
          console.log('Photos uploaded successfully');
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          // Continue anyway - article is created
        }
      }

      toast({
        title: "Story Created!",
        description: "Review your AI-generated article and publish when ready."
      });

      // Navigate to review page
      const articleId = result._id || result.id;
      console.log('Navigating to review page with ID:', articleId);
      
      // Give user a moment to see the success message
      setTimeout(() => {
        window.location.href = `/review-article/${articleId}`;
      }, 1000);
      
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to create story. Please try again.",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      {/* Header */}
      <div className="text-center mb-12">
        <Heart size={48} className="mx-auto mb-4 text-red-500" />
        <h1 className="text-4xl font-serif font-bold mb-4">
          Share Good News
        </h1>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Celebrate your kids' achievements, local heroes, community events, and good deeds. 
          Give everyday Central Coast residents a platform to be seen and celebrated.
        </p>
      </div>

      {/* Examples */}
      <Card className="mb-8 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="text-green-600" size={20} />
            What Can You Share?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong className="text-green-700">🏆 Youth Sports</strong>
              <p className="text-gray-700 mt-1">Game-winning goals, championships, team spirit, personal bests</p>
            </div>
            <div>
              <strong className="text-blue-700">❤️ Local Heroes</strong>
              <p className="text-gray-700 mt-1">Neighbors helping neighbors, volunteers, everyday heroes</p>
            </div>
            <div>
              <strong className="text-purple-700">🎉 Community Events</strong>
              <p className="text-gray-700 mt-1">Festivals, fundraisers, celebrations, gatherings</p>
            </div>
            <div>
              <strong className="text-orange-700">✨ Good Deeds</strong>
              <p className="text-gray-700 mt-1">Acts of kindness, helping others, making a difference</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Submission Form */}
      <Card>
        <CardHeader>
          <CardTitle>Tell Us Your Good News</CardTitle>
          <CardDescription>
            AI will write a beautiful article celebrating your story
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="submitter_name">Your Name *</Label>
                <Input
                  id="submitter_name"
                  value={formData.submitter_name}
                  onChange={(e) => setFormData({...formData, submitter_name: e.target.value})}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div>
                <Label htmlFor="submitter_email">Email Address *</Label>
                <Input
                  id="submitter_email"
                  type="email"
                  value={formData.submitter_email}
                  onChange={(e) => setFormData({...formData, submitter_email: e.target.value})}
                  placeholder="your.email@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="story_type">Story Type *</Label>
              <select
                id="story_type"
                value={formData.story_type}
                onChange={(e) => setFormData({...formData, story_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                required
              >
                <option value="youth-sports">Youth Sports Achievement</option>
                <option value="local-heroes">Local Hero</option>
                <option value="community-events">Community Event</option>
                <option value="good-deeds">Good Deed / Act of Kindness</option>
              </select>
            </div>

            <div>
              <Label htmlFor="headline">Story Headline *</Label>
              <Input
                id="headline"
                value={formData.headline}
                onChange={(e) => setFormData({...formData, headline: e.target.value})}
                placeholder="e.g., '12-Year-Old Sarah Mitchell Scores Hat Trick to Win Championship'"
                required
              />
            </div>

            {/* AI vs Manual Toggle */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <Label className="text-base font-semibold mb-3 block">How would you like to create your article?</Label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setUseAI(true)}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    useAI 
                      ? 'border-blue-600 bg-blue-50 text-blue-900' 
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <Sparkles className={`mx-auto mb-2 ${useAI ? 'text-blue-600' : 'text-gray-400'}`} size={24} />
                    <div className="font-semibold">AI-Assisted</div>
                    <div className="text-sm mt-1">Share your story details, AI writes the article</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setUseAI(false)}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    !useAI 
                      ? 'border-blue-600 bg-blue-50 text-blue-900' 
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <div className="text-center">
                    <Heart className={`mx-auto mb-2 ${!useAI ? 'text-blue-600' : 'text-gray-400'}`} size={24} />
                    <div className="font-semibold">Write It Myself</div>
                    <div className="text-sm mt-1">I'll write the complete article myself</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Conditional Fields Based on Choice */}
            {useAI ? (
              <>
                <div>
                  <Label htmlFor="story">Tell Us What Happened *</Label>
                  <Textarea
                    id="story"
                    value={formData.story}
                    onChange={(e) => setFormData({...formData, story: e.target.value})}
                    placeholder="Share the details... Who, what, when, where, why? What made this special? How did it impact the community?"
                    rows={8}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Don't worry about perfect writing - just share the facts and feelings. AI will make it beautiful!
                  </p>
                </div>

                <div>
                  <Label htmlFor="key_details">Key Details (Optional)</Label>
                  <Textarea
                    id="key_details"
                    value={formData.key_details}
                    onChange={(e) => setFormData({...formData, key_details: e.target.value})}
                    placeholder="Names, ages, team names, scores, locations, dates, quotes from people involved..."
                    rows={4}
                  />
                </div>
              </>
            ) : (
              <div>
                <Label htmlFor="manual_article">Write Your Article *</Label>
                <Textarea
                  id="manual_article"
                  value={formData.manual_article}
                  onChange={(e) => setFormData({...formData, manual_article: e.target.value})}
                  placeholder="Write your complete article here... Include all details, quotes, and narrative."
                  rows={20}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Write your article in full - it will be published as you've written it (with light editing for formatting).
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="photos">Upload Photos (Optional)</Label>
              <input
                id="photos"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setPhotos(files);
                  
                  // Create previews
                  const previews = files.map(file => URL.createObjectURL(file));
                  setPhotoPreviews(previews);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
              />
              <p className="text-xs text-gray-500 mt-1">
                Upload up to 5 photos to showcase your story (JPG, PNG)
              </p>
              
              {/* Photo Previews */}
              {photoPreviews.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {photoPreviews.map((preview, index) => (
                    <div key={index} className="relative">
                      <img src={preview} alt={`Preview ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => {
                          const newPhotos = [...photos];
                          const newPreviews = [...photoPreviews];
                          newPhotos.splice(index, 1);
                          newPreviews.splice(index, 1);
                          setPhotos(newPhotos);
                          setPhotoPreviews(newPreviews);
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-green-600 hover:bg-green-700" 
              size="lg"
              disabled={loading}
            >
              {loading ? (
                <>Creating Your Story...</>
              ) : (
                <>
                  <Heart className="mr-2" size={16} />
                  Share Good News
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Bottom Message */}
      <div className="mt-8 text-center">
        <p className="text-gray-600">
          Your story will be reviewed by AI and published to celebrate the Central Coast community. 
          No negativity, no bias - just good news that deserves to be seen.
        </p>
      </div>
    </div>
  );
};

export default ShareGoodNewsPage;
